import { FunctionComponent, memo, useState } from "react";
import { Button, Tooltip } from "@edgetier/components";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { Route } from "@edgetier/watchtower-types";
import toast from "react-hot-toast";

import { axios } from "utilities/axios";

import { IProps } from "./download-interactions-button.types";
import { INTERACTION_DOWNLOAD_LIMIT } from "./download-interactions-button.constants";
import { parseFilename } from "./download-interactions-button.utilities";
import "./download-interactions-button.scss";

/**
 * A button to download the interactions to a CSV file.
 * @param props.parameters The parameters to be applied to the request.
 */
const DownloadInteractionsButton: FunctionComponent<IProps> = ({ parameters }) => {
    const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);

    /**
     * Request the interactions from the backend in a CSV format and trigger the download.
     */
    const downloadInteractions = async () => {
        try {
            setDownloadInProgress(true);
            const { data, headers } = await axios.get<string>(Route.Interactions, {
                headers: { Accept: "text/csv" },
                params: { ...parameters, limit: INTERACTION_DOWNLOAD_LIMIT },
            });

            const filename = parseFilename(headers["content-disposition"]);

            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);

            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Failed to download interactions");
        } finally {
            setDownloadInProgress(false);
        }
    };

    return (
        <div className="download-interactions-button">
            <Tooltip content={`Download Interactions (${INTERACTION_DOWNLOAD_LIMIT} row limit)`} delayEnter={500}>
                <Button
                    icon={faDownload}
                    isIconOnly
                    onClick={downloadInteractions}
                    outline
                    size="small"
                    isLoading={downloadInProgress}
                    disabled={downloadInProgress}
                    aria-label="download-interactions"
                />
            </Tooltip>
        </div>
    );
};

export default memo(DownloadInteractionsButton);
